import { ReactElement, ReactNode, startTransition } from "react"

import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isUndefined from "lodash-es/isUndefined"
import { type Location, Navigate, type NavigateFunction, matchPath, useLocation, useNavigate } from "react-router-dom"

import { isNullOrUndefined } from "$/utils/gates"
import { getIndexPage } from "$/utils/get-index-page"

import { PRODUCT_TYPES_WITH_ENROLLMENTS } from "@/3514/config"
import { EPath, ERoute } from "@/3514/constants"
import { type IAppState, useAppSelector } from "@/3514/store/app"
import { EProductTypeInternalName, type IEnrollmentModel, IUserModel, IUserParticipantModel } from "@/3514/types"
import { isUserParticipant } from "@/3514/utils"
import { INDEX_URL, ParticipantUrls } from "@/constants"
import { EStorageServiceType, LocalStorageServiceKeys, SessionStorageServiceKeys, StorageService } from "@/services"
import PageLoader from "@/shared/spinner/PageLoader"

type Props = {
    isAllowed: boolean
    redirectPath?: string
    children?: ReactElement
    isLoading?: boolean
    user: IUserModel
    loadingFallback?: ReactNode
}

const onboardingUrls: string[] = [
    ERoute.Participant_OnboardingProfile,
    ERoute.Participant_OnboardingVideo,
    ERoute.Participant_OnboardingModules_Legacy,
    ERoute.Participant_OnboardingDiagnostic,
    ERoute.Participant_OnboardingDiagnosticResults_Legacy,
    ERoute.Participant_OnboardingReflection,
    ERoute.Participant_OnboardingHolds,
    ERoute.Participant_OnboardingCoach,
    ERoute.Participant_OnboardingRedirect
]

const localStorageService: StorageService = new StorageService(EStorageServiceType.Local)
const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

const ProtectedRoute = ({
    children,
    isAllowed,
    redirectPath = EPath.Login({ isErrorReset: false }),
    user,
    isLoading,
    loadingFallback = undefined
}: Props) => {
    const location: Location = useLocation()

    const isParticipant: boolean = isUserParticipant(user)

    const { appInit }: IAppState = useAppSelector()
    const isIndex: boolean = location.pathname === INDEX_URL
    const isWaitingListPage =
        matchPath(ParticipantUrls.WAITING_LIST_SSO, location.pathname) ||
        matchPath(ParticipantUrls.WAITING_LIST, location.pathname)

    const navigate: NavigateFunction = useNavigate()

    if (isLoading) return loadingFallback ?? <PageLoader />

    if (
        !user &&
        !localStorageService.getItem<string>(LocalStorageServiceKeys.TokenAccess()) &&
        !location.pathname.includes("register")
    )
        return (
            <Navigate
                to={EPath.Login({ isErrorReset: false })}
                state={
                    sessionStorageService.getItem<string>(SessionStorageServiceKeys.UserLogout())
                        ? null
                        : { from: `${location.pathname}${location.search ?? String()}`, ...location }
                }
                replace
            />
        )

    if (!appInit) return loadingFallback ?? <PageLoader />

    if (isParticipant && !isWaitingListPage)
        if (!(user as IUserParticipantModel).doesCohortExist)
            return (
                <Navigate
                    to={
                        (user as IUserParticipantModel).isUnexpectedAuth
                            ? `${ParticipantUrls.WAITING_LIST}/unrecognized-sso/${(user as IUserParticipantModel).organizationSlug}`
                            : ParticipantUrls.WAITING_LIST
                    }
                    state={{ from: location }}
                    replace
                />
            )

    const hasEnrolledModules: boolean = includes<EProductTypeInternalName>(
        PRODUCT_TYPES_WITH_ENROLLMENTS,
        (user as IUserParticipantModel).productType
    )

    if (
        (user as IUserParticipantModel).isEligibleSelectModule &&
        hasEnrolledModules &&
        location.pathname !== ParticipantUrls.ASSIGN_MODULE
    )
        return <Navigate to={ParticipantUrls.ASSIGN_MODULE} replace />

    if (
        !isNullOrUndefined(matchPath(ParticipantUrls.SWAP_SESSION, location.pathname)) &&
        !(user as IUserParticipantModel).areGuestSessionsEnabled
    )
        return <Navigate to={EPath.Participant_CoachingSessions()} replace />

    if (isParticipant && (user as IUserParticipantModel).areNotCompletedSurveysPresent) {
        if (!localStorageService.getItem(LocalStorageServiceKeys.SurveysPageRedirected(user.id))) {
            setTimeout(
                (): void => startTransition((): void => void navigate(ParticipantUrls.SURVEYS, { replace: true })),
                0
            )
            localStorageService.setItem(LocalStorageServiceKeys.SurveysPageRedirected(user.id), true)
        }
    }

    if (isIndex) {
        if (user) return <Navigate to={getIndexPage(user, true)} state={{ from: location }} replace />
        return <Navigate to={EPath.Login({ isErrorReset: false })} state={{ from: location }} replace />
    }

    if (user.role === "participant" && !location.pathname.includes(ParticipantUrls.WAITING_LIST)) {
        if (!user.isOnboardingFinished && !onboardingUrls.includes(location.pathname))
            if (!user.isEligibleSelectModule)
                return <Navigate to={EPath.Participant_OnboardingRedirect()} state={{ from: location }} replace />

        if (user.isOnboardingFinished) {
            if (onboardingUrls.includes(location.pathname)) {
                if (user.areModulesAvailable)
                    return <Navigate to={EPath.Participant_Dashboard()} state={{ from: location }} replace />
                if (user.areNotCompletedSurveysPresent)
                    return <Navigate to={ParticipantUrls.SURVEYS} state={{ from: location }} replace />
                return <Navigate to={EPath.Participant_CoachingSessions()} state={{ from: location }} replace />
            }

            if (
                user.currentModule &&
                (location.pathname === ERoute.Participant_Dashboard ||
                    location.pathname === `${ERoute.Participant_Dashboard}/`)
            ) {
                return (
                    <Navigate
                        to={EPath.Participant_DashboardModule(user.currentModule.rank)}
                        state={{ from: location }}
                        replace
                    />
                )
            }

            if (
                !user?.currentModule &&
                (location.pathname === ERoute.Participant_Dashboard ||
                    location.pathname === `${ERoute.Participant_Dashboard}/`)
            ) {
                const lastEnrolledModule: IEnrollmentModel = isEmpty(user.enrollments)
                    ? undefined
                    : user.enrollments?.findLast(({ status }: IEnrollmentModel): boolean => status !== "unassigned")

                if (!isUndefined(lastEnrolledModule)) {
                    return (
                        <Navigate
                            to={EPath.Participant_DashboardModule(lastEnrolledModule?.rank)}
                            state={{ from: location }}
                            replace
                        />
                    )
                }
            }
        }
    }

    if (!isAllowed) {
        return <Navigate to={redirectPath} state={{ from: location }} replace />
    }

    return children
}

export default ProtectedRoute
